import { resolver } from "@msuite/ui/libs/form";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { guardNull } from "@msuite/utils/misc";
import { projectMemberControllerSchema } from "@msuite/utils/schemas";
import { useForm } from "react-hook-form";
import { client } from "@/hono";

/** Props Interface */
interface UseProjectMemberControllerProps {
	projectId: string | null;
}

export function useProjectMemberController({ projectId }: UseProjectMemberControllerProps) {
	/** Hooks */
	const controller = useForm({
		defaultValues: async () => {
			return await defaultValues(projectId);
		},
		resolver: resolver(projectMemberControllerSchema),
	});

	/** Functions */
	async function handleOnSubmit(projectId: string) {
		try {
			const isValid = await controller.trigger();
			if (!isValid) return;
			await client.projects[":projectId"].members.$patch({
				param: { projectId },
				json: controller.getValues(),
			});
		} catch (error) {
			toast.error("Could not update project memberships");
			console.error(error);
		}
	}

	async function handleOnReset(projectId: string) {
		const values = await defaultValues(projectId);
		controller.reset(values);
	}

	async function defaultValues(projectId: string | null) {
		if (!projectId) {
			return {
				manager: null,
				deputyManagers: [],
			};
		}
		const response = await client.projects[":projectId"].members.$get({
			param: { projectId },
		});
		const data = await responseHandler(response);
		return {
			manager: guardNull(data?.manager),
			deputyManagers: guardArray(data?.deputyManagers),
		};
	}

	/** Return */
	return {
		controller,
		handleOnSubmit,
		handleOnReset,
	};
}

export type ProjectMemberController = ReturnType<typeof useProjectMemberController>["controller"];
